import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../notification/notification.service';
import { ProcurementService, ProcurementQueue, ProcurementTask } from '../api';

// ============================================================================
// Beszerzés — a vásárolt (nem gyártott) alkatrészek utántöltési feladatai.
// A Gyártás a nyomtatott tételeket viszi; ami vásárolt (nincs nyomtatási ideje
// és anyaga), arra itt keletkezik feladat. A cél ugyanaz a képlet, mint a
// gyártásnál: cél = készletminimum + aktív rendelések foglalása.
// ============================================================================

type PurchaseTone = 'urgent' | 'out' | 'low';

interface PurchaseRow {
  task: ProcurementTask;
  tone: PurchaseTone;
  badgeLabel: string;
  badgeClass: string;
  meta: string;
  costLabel: string;
  pct: number;
  progRed: boolean;
  progLabel: string;
  /** ebben a munkamenetben bevételezett darabszám */
  justReceived: number;
  /** elérte a célt — a sor a helyén marad a következő frissítésig */
  done: boolean;
}

@Component({
  selector: 'app-beszerzes',
  templateUrl: './beszerzes.component.html',
  styleUrl: './beszerzes.component.scss'
})
export class BeszerzesComponent implements OnInit {
  loading = true;
  loadFailed = false;
  rows: PurchaseRow[] = [];

  totalPieces = 0;
  taskCount = 0;
  costLabel = '0 Ft';
  missingPriceCount = 0;

  /** megérkezett db inputok SKU-nként */
  inputs: Record<string, string> = {};
  /** folyamatban lévő bevételezés SKU-nként */
  pending: Record<string, boolean> = {};

  constructor(
    private procurement: ProcurementService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.procurement.getQueue().subscribe({
      next: (queue) => {
        this.build(queue);
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.totalPieces = 0;
        this.taskCount = 0;
        this.costLabel = '0 Ft';
        this.missingPriceCount = 0;
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  /** Kézi frissítés: ekkor rendeződik újra a lista és esnek ki a kész tételek. */
  refresh(): void {
    this.inputs = {};
    this.load();
  }

  private build(queue: ProcurementQueue): void {
    const tasks = (queue?.tasks || []).filter(t => (t.need || 0) > 0);

    this.taskCount = tasks.length;
    this.totalPieces = queue?.totalPieces ?? tasks.reduce((s, t) => s + (t.need || 0), 0);
    this.missingPriceCount = queue?.missingPriceCount ?? 0;
    this.costLabel = this.formatHuf(
      queue?.totalCost ?? tasks.reduce((s, t) => s + (t.estimatedCost || 0), 0));

    this.rows = tasks.map(t => this.makeRow(t));

    // A listáról lekerült tételek beírt értékeit eldobjuk, hogy egy később
    // visszatérő alkatrésznél ne bukkanjon fel a régi, már bevételezett szám.
    const liveSkus = new Set(this.rows.map(row => row.task.sku));
    Object.keys(this.inputs).forEach(sku => { if (!liveSkus.has(sku)) { delete this.inputs[sku]; } });
    Object.keys(this.pending).forEach(sku => { if (!liveSkus.has(sku)) { delete this.pending[sku]; } });
  }

  private makeRow(t: ProcurementTask, justReceived = 0): PurchaseRow {
    const stock = t.stock || 0;
    const target = Math.max(1, t.target || 0);
    const tone: PurchaseTone = t.urgent ? 'urgent' : (t.out ? 'out' : 'low');
    const badge = t.urgent
      ? { label: 'Sürgős · rendelés vár rá', cls: 'stk-pill--red' }
      : (t.out
        ? { label: 'Elfogyott', cls: 'stk-pill--red' }
        : { label: 'Fogyóban', cls: 'stk-pill--yellow' });
    return {
      task: t,
      tone,
      badgeLabel: badge.label,
      badgeClass: badge.cls,
      meta: t.sku + (t.purchasePrice !== null ? ' · ' + this.formatHuf(t.purchasePrice) + ' / db' : ' · nincs megadva beszerzési ár'),
      costLabel: t.estimatedCost !== null
        ? '≈ ' + this.formatHuf(t.estimatedCost)
        : 'ár nélkül',
      pct: Math.max(0, Math.min(100, Math.round(stock / target * 100))),
      progRed: !!(t.urgent || t.out),
      progLabel: stock + ' / ' + (t.target || 0) + ' db · min. ' + (t.minStock || 0) + ' db'
        + ((t.reserved || 0) > 0 ? ' + ' + t.reserved + ' db foglalt rendelésekhez' : ''),
      justReceived,
      done: (t.need || 0) <= 0,
    };
  }

  /** A sorok azonosítása SKU szerint — a beírómezők így nem csúszhatnak át. */
  trackBySku = (_index: number, row: PurchaseRow) => row.task.sku;

  onQtyInput(sku: string, input: HTMLInputElement): void {
    const digits = input.value.replace(/[^0-9]/g, '');
    input.value = digits;
    this.inputs[sku] = digits;
  }

  qtyOf(sku: string): number {
    return parseInt(this.inputs[sku] || '', 10) || 0;
  }

  canReceive(sku: string): boolean {
    return this.qtyOf(sku) > 0 && !this.pending[sku];
  }

  /** A beírt mező feltöltése a hiányzó mennyiséggel — egy kattintás. */
  fillNeeded(row: PurchaseRow): void {
    this.inputs[row.task.sku] = `${row.task.need || 0}`;
  }

  /** Beérkezett áru bevételezése. */
  receive(row: PurchaseRow): void {
    const sku = row.task.sku;
    const quantity = this.qtyOf(sku);
    if (quantity <= 0 || this.pending[sku]) { return; }
    this.pending[sku] = true;
    this.procurement.receiveStock({ sku, quantity }).subscribe({
      next: (response) => {
        this.pending[sku] = false;
        this.inputs[sku] = '';
        this.notification.show(
          `${quantity} db ${row.task.name} bevételezve.`,
          'success',
        );
        // A listát szándékosan NEM töltjük újra: a sorok a helyükön maradnak,
        // hogy a gombok ne csússzanak el a kurzor alól.
        this.applyReceived(sku, quantity, response?.newStock);
      },
      error: () => {
        this.pending[sku] = false;
        this.notification.show('A bevételezés nem sikerült.', 'danger');
      },
    });
  }

  private applyReceived(sku: string, quantity: number, newStock?: number): void {
    const index = this.rows.findIndex(row => row.task.sku === sku);
    if (index < 0) { return; }
    const previous = this.rows[index];
    const stock = Number.isFinite(Number(newStock))
      ? Number(newStock)
      : (previous.task.stock || 0) + quantity;
    const target = previous.task.target || 0;
    const need = Math.max(0, target - stock);
    const updated: ProcurementTask = {
      ...previous.task,
      stock,
      need,
      estimatedCost: previous.task.purchasePrice !== null
        ? Math.round(need * previous.task.purchasePrice)
        : null,
      out: stock <= 0,
      urgent: stock <= 0 && (previous.task.reserved || 0) > 0,
    };
    this.rows[index] = this.makeRow(updated, previous.justReceived + quantity);
    this.recomputeTotals();
  }

  private recomputeTotals(): void {
    const open = this.rows.filter(row => (row.task.need || 0) > 0);
    this.taskCount = open.length;
    this.totalPieces = open.reduce((sum, row) => sum + (row.task.need || 0), 0);
    this.costLabel = this.formatHuf(open.reduce((sum, row) => sum + (row.task.estimatedCost || 0), 0));
    this.missingPriceCount = open.filter(row => row.task.purchasePrice === null).length;
  }

  get hasCompletedRows(): boolean {
    return this.rows.some(row => row.done);
  }

  private formatHuf(value: number): string {
    return Math.round(value || 0).toLocaleString('hu-HU') + ' Ft';
  }
}
